import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "print-plug-cart-v3";
const SAVED_KEY = "print-plug-saved-v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [savedItems, setSavedItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(SAVED_KEY, JSON.stringify(savedItems));
  }, [savedItems]);

  const addItem = (product, variant) => {
    const cartKey = variant ? `${product.product_id}:${variant.variant_id}` : product.product_id;
    const price = variant ? variant.price_cents : product.price_cents;
    const name = variant ? `${product.name} — ${variant.label}` : product.name;
    setItems((prev) => {
      const existing = prev.find((i) => i.cart_key === cartKey);
      if (existing) {
        return prev.map((i) =>
          i.cart_key === cartKey ? { ...i, qty: Math.min(100, i.qty + 1) } : i
        );
      }
      return [
        ...prev,
        {
          cart_key: cartKey,
          product_id: product.product_id,
          variant_id: variant ? variant.variant_id : null,
          variant_label: variant ? variant.label : null,
          is_digital: Boolean(product.digital_files),
          name,
          price_cents: price,
          image: (variant && variant.image) || product.image,
          qty: 1,
        },
      ];
    });
    toast.success("Added to cart", { description: name });
  };

  const setQty = (cartKey, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.cart_key !== cartKey));
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.cart_key === cartKey ? { ...i, qty: Math.min(100, qty) } : i))
    );
  };

  const removeItem = (cartKey) =>
    setItems((prev) => prev.filter((i) => i.cart_key !== cartKey));

  const saveForLater = (cartKey) => {
    setItems((prev) => {
      const item = prev.find((i) => i.cart_key === cartKey);
      if (item) {
        setSavedItems((s) => [...s.filter((x) => x.cart_key !== cartKey), item]);
        toast.success("Saved for later", { description: item.name });
      }
      return prev.filter((i) => i.cart_key !== cartKey);
    });
  };

  const moveToCart = (cartKey) => {
    setSavedItems((prev) => {
      const item = prev.find((i) => i.cart_key === cartKey);
      if (item) {
        setItems((its) => {
          const existing = its.find((i) => i.cart_key === cartKey);
          if (existing) {
            return its.map((i) => (i.cart_key === cartKey ? { ...i, qty: Math.min(100, i.qty + item.qty) } : i));
          }
          return [...its, item];
        });
        toast.success("Moved back to cart", { description: item.name });
      }
      return prev.filter((i) => i.cart_key !== cartKey);
    });
  };

  const removeSaved = (cartKey) =>
    setSavedItems((prev) => prev.filter((i) => i.cart_key !== cartKey));

  const clear = () => setItems([]);

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const totalCents = items.reduce((s, i) => s + i.qty * i.price_cents, 0);
    return { items, savedItems, addItem, setQty, removeItem, saveForLater, moveToCart, removeSaved, clear, count, totalCents };
  }, [items, savedItems]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
